import {
  UseCriteriaQueryOptions,
  useCriteriaQuery,
} from 'hooks/reviews/useCriteriaCache';
import type {
  Criteria,
  CriteriaFramework,
} from 'concepts/Criteria/framework/CriteriaFramework';

type useCriteriaResult = {
  isLoading: boolean;
  hasCriteria: boolean;
  hasFramework: boolean;
  criteria?: Criteria;
  framework?: CriteriaFramework;
  version?: string;
  protocolLink?: string;
  fetchingFailed: boolean;
  error?: unknown;
  refetch: () => void;
};

const hasText = (entry: string): boolean => entry.trim().length > 0;

// Matches Rails' "non-blank criteria" gate for suggested highlights (422 no_criteria).
const hasAnyCriteria = (criteria?: Criteria): boolean =>
  !!criteria &&
  Object.values(criteria).some(
    (item) => item.include.some(hasText) || item.exclude.some(hasText)
  );

export function useCriteria(
  options?: UseCriteriaQueryOptions
): useCriteriaResult {
  const { isLoading, data, error, isError, refetch } = useCriteriaQuery(
    options
  );

  const criteria = data?.criteria;
  const framework = data?.framework;
  const version = data?.version;
  const protocolLink = data?.protocolLink;

  const hasCriteria = hasAnyCriteria(criteria);

  // Not isFetching: a background refetch would re-render every subscriber
  // even when the data is unchanged.
  return {
    isLoading,
    hasCriteria,
    hasFramework: !!framework,
    criteria,
    framework,
    version,
    protocolLink,
    // A failure only counts as a failed load when nothing has loaded yet.
    // React-query keeps data through a failed background refetch (e.g. on
    // window refocus) while flipping isError; treating that as a failed load
    // would unmount the already-populated form and discard unsaved edits.
    fetchingFailed: isError && data === undefined,
    error,
    refetch,
  };
}
